// src/pages/finance-portal/fixed-assets/AssetCategories.js
//
// Asset Categorization Engine

import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Card, Dialog, DialogActions, DialogContent,
    DialogTitle, Grid, IconButton, MenuItem, Paper, Skeleton, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
    Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryIcon from '@mui/icons-material/Category';
import { useSnackbar } from 'notistack';

import axiosInstance from '../../../utils/axiosInstance';
import {
    FA_FONT, FA_ACCENT, FA_ACCENT_DARK, ASSET_CLASS_LABELS,
    DEPRECIATION_METHODS, DEPRECIATION_CONVENTIONS,
    formatCurrency, humanizeStatus,
} from './components/fixedAssetFormatters';

const headSx = { fontFamily: FA_FONT, fontWeight: 700, fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' };
const cellSx = { fontFamily: FA_FONT, fontSize: '0.84rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9' };

const empty = {
    name: '', slug: '', asset_class: 'OTHER',
    default_useful_life_months: '', default_residual_value_pct: 0,
    default_depreciation_method: 'STRAIGHT_LINE',
    default_convention: 'FULL_MONTH',
    default_tax_method: 'MACRS_GDS',
    default_tax_recovery_period_years: '',
    capitalization_threshold: 2500,
    is_active: true,
};

export default function AssetCategories() {
    const { enqueueSnackbar } = useSnackbar();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [errors, setErrors] = useState({});
    const [editId, setEditId] = useState(null);

    const setField = (k, v) => {
        setForm(prev => ({ ...prev, [k]: v }));
        if (errors[k]) {
            setErrors(prev => {
                const next = { ...prev };
                delete next[k];
                return next;
            });
        }
    };

    const validateForm = () => {
        const e = {};
        if (!form.name || !form.name.trim()) {
            e.name = 'Category name is required';
        } else if (form.name.length > 150) {
            e.name = 'Name must be 150 characters or fewer';
        }

        const life = form.default_useful_life_months;
        if (life !== '' && life !== null && life !== undefined) {
            if (!/^\d+$/.test(String(life)) || parseInt(life, 10) <= 0) {
                e.default_useful_life_months = 'Useful life must be a positive whole number';
            }
        }

        const recovery = form.default_tax_recovery_period_years;
        if (recovery !== '' && recovery !== null && recovery !== undefined) {
            if (!/^\d+$/.test(String(recovery)) || parseInt(recovery, 10) <= 0) {
                e.default_tax_recovery_period_years = 'Recovery period must be a positive whole number';
            }
        }

        const residualPct = parseFloat(form.default_residual_value_pct);
        if (form.default_residual_value_pct !== '' && form.default_residual_value_pct !== null) {
            if (Number.isNaN(residualPct) || residualPct < 0 || residualPct > 100) {
                e.default_residual_value_pct = 'Residual % must be between 0 and 100';
            }
        }

        const threshold = parseFloat(form.capitalization_threshold);
        if (form.capitalization_threshold !== '' && form.capitalization_threshold !== null) {
            if (Number.isNaN(threshold) || threshold < 0) {
                e.capitalization_threshold = 'Threshold must be a non-negative number';
            }
        }

        return e;
    };

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/fixed-assets/categories');
            if (res.data?.success) setRows(res.data.data || []);
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Failed to load categories', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    useEffect(() => { fetchRows(); }, [fetchRows]);

    const openCreate = () => { setEditId(null); setForm(empty); setErrors({}); setOpen(true); };
    const openEdit = (cat) => {
        setEditId(cat.id);
        setForm({ ...empty, ...cat });
        setErrors({});
        setOpen(true);
    };

    const handleSave = async () => {
        const fieldErrors = validateForm();
        if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
            enqueueSnackbar(`Fix ${Object.keys(fieldErrors).length} validation error(s)`,
                { variant: 'warning' });
            return;
        }
        setErrors({});
        try {
            const payload = {
                ...form,
                default_useful_life_months: form.default_useful_life_months
                    ? parseInt(form.default_useful_life_months, 10) : null,
                default_tax_recovery_period_years: form.default_tax_recovery_period_years
                    ? parseInt(form.default_tax_recovery_period_years, 10) : null,
                default_residual_value_pct: parseFloat(form.default_residual_value_pct) || 0,
                capitalization_threshold: parseFloat(form.capitalization_threshold) || 0,
            };
            const res = editId
                ? await axiosInstance.put(`/api/finance/fixed-assets/categories/${editId}`, payload)
                : await axiosInstance.post('/api/finance/fixed-assets/categories', payload);
            if (res.data?.success) {
                enqueueSnackbar(editId ? 'Category updated' : 'Category created', { variant: 'success' });
                setOpen(false);
                fetchRows();
            }
        } catch (e) {
            const serverErrors = e.response?.data?.errors;
            if (serverErrors && typeof serverErrors === 'object') {
                setErrors(prev => ({ ...prev, ...serverErrors }));
                enqueueSnackbar(e.response?.data?.message || 'Validation failed on server',
                    { variant: 'warning' });
            } else {
                enqueueSnackbar(e.response?.data?.message || 'Save failed', { variant: 'error' });
            }
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this category?')) return;
        try {
            await axiosInstance.delete(`/api/finance/fixed-assets/categories/${id}`);
            enqueueSnackbar('Category deleted', { variant: 'success' });
            fetchRows();
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Delete failed', { variant: 'error' });
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: `linear-gradient(135deg, ${FA_ACCENT} 0%, ${FA_ACCENT_DARK} 100%)`,
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: 3, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CategoryIcon sx={{ fontSize: 30, color: '#fff' }} />
                        <Typography sx={{ fontFamily: FA_FONT, fontWeight: 800, color: 'white', fontSize: '1.6rem' }}>
                            Asset Categories
                        </Typography>
                    </Box>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
                        sx={{ fontFamily: FA_FONT, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: 'white', color: FA_ACCENT, '&:hover': { bgcolor: 'rgba(255,255,255,0.92)' } }}>
                        New Category
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1300, mx: 'auto' }}>
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Name', 'Class', 'Useful Life', 'Method', 'Cap. Threshold', 'Tax Recovery', ''].map(h => (
                                        <TableCell key={h} sx={headSx}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? [1, 2, 3].map(i => (
                                    <TableRow key={i}>{[1, 2, 3, 4, 5, 6, 7].map(j => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
                                )) : rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                                            <Typography sx={{ fontFamily: FA_FONT, color: '#94a3b8' }}>No categories yet</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : rows.map(c => (
                                    <TableRow key={c.id} hover>
                                        <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{c.name}</TableCell>
                                        <TableCell sx={cellSx}>{ASSET_CLASS_LABELS[c.asset_class] || humanizeStatus(c.asset_class)}</TableCell>
                                        <TableCell sx={cellSx}>{c.default_useful_life_months ? `${c.default_useful_life_months} mo` : '\u2014'}</TableCell>
                                        <TableCell sx={cellSx}>{humanizeStatus(c.default_depreciation_method)}</TableCell>
                                        <TableCell sx={cellSx}>{formatCurrency(c.capitalization_threshold)}</TableCell>
                                        <TableCell sx={cellSx}>{c.default_tax_recovery_period_years ? `${c.default_tax_recovery_period_years} yr` : '\u2014'}</TableCell>
                                        <TableCell sx={cellSx}>
                                            <Tooltip title="Edit">
                                                <IconButton size="small" onClick={() => openEdit(c)}>
                                                    <EditIcon sx={{ fontSize: 18, color: '#475569' }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton size="small" onClick={() => handleDelete(c.id)}>
                                                    <DeleteIcon sx={{ fontSize: 18, color: '#dc2626' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Box>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily: FA_FONT, fontWeight: 700 }}>
                    {editId ? 'Edit Category' : 'New Category'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth size="small" label="Name *" required
                                value={form.name} onChange={e => setField('name', e.target.value)}
                                error={Boolean(errors.name)} helperText={errors.name || ' '}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth size="small" select label="Asset Class"
                                value={form.asset_class} onChange={e => setField('asset_class', e.target.value)}
                                helperText=" "
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }}>
                                {Object.entries(ASSET_CLASS_LABELS).map(([k, v]) => (
                                    <MenuItem key={k} value={k} sx={{ fontFamily: FA_FONT }}>{v}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <TextField fullWidth size="small" type="number" label="Useful Life (mo)"
                                value={form.default_useful_life_months}
                                onChange={e => setField('default_useful_life_months', e.target.value)}
                                error={Boolean(errors.default_useful_life_months)}
                                helperText={errors.default_useful_life_months || ' '}
                                inputProps={{ min: 0, step: 1 }}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <TextField fullWidth size="small" type="number" label="Residual %"
                                value={form.default_residual_value_pct}
                                onChange={e => setField('default_residual_value_pct', e.target.value)}
                                error={Boolean(errors.default_residual_value_pct)}
                                helperText={errors.default_residual_value_pct || ' '}
                                inputProps={{ min: 0, max: 100, step: '0.01' }}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <TextField fullWidth size="small" type="number" label="Cap. Threshold"
                                value={form.capitalization_threshold}
                                onChange={e => setField('capitalization_threshold', e.target.value)}
                                error={Boolean(errors.capitalization_threshold)}
                                helperText={errors.capitalization_threshold || ' '}
                                inputProps={{ min: 0, step: '0.01' }}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <TextField fullWidth size="small" type="number" label="Tax Recovery (yrs)"
                                value={form.default_tax_recovery_period_years}
                                onChange={e => setField('default_tax_recovery_period_years', e.target.value)}
                                error={Boolean(errors.default_tax_recovery_period_years)}
                                helperText={errors.default_tax_recovery_period_years || ' '}
                                inputProps={{ min: 0, step: 1 }}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField fullWidth size="small" select label="Book Method"
                                value={form.default_depreciation_method}
                                onChange={e => setForm({ ...form, default_depreciation_method: e.target.value })}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }}>
                                {DEPRECIATION_METHODS.map(m => <MenuItem key={m.value} value={m.value} sx={{ fontFamily: FA_FONT }}>{m.label}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={4}>
                            <TextField fullWidth size="small" select label="Convention"
                                value={form.default_convention}
                                onChange={e => setForm({ ...form, default_convention: e.target.value })}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }}>
                                {DEPRECIATION_CONVENTIONS.map(c => <MenuItem key={c.value} value={c.value} sx={{ fontFamily: FA_FONT }}>{c.label}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={4}>
                            <TextField fullWidth size="small" select label="Tax Method"
                                value={form.default_tax_method}
                                onChange={e => setForm({ ...form, default_tax_method: e.target.value })}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }}>
                                {DEPRECIATION_METHODS.map(m => <MenuItem key={m.value} value={m.value} sx={{ fontFamily: FA_FONT }}>{m.label}</MenuItem>)}
                            </TextField>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setOpen(false)} sx={{ fontFamily: FA_FONT }}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained"
                        sx={{ fontFamily: FA_FONT, textTransform: 'none', bgcolor: FA_ACCENT,
                            '&:hover': { bgcolor: FA_ACCENT_DARK }, borderRadius: '10px' }}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
